import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FaChevronLeft, FaChevronRight, FaTimes, FaExternalLinkAlt } from 'react-icons/fa';
import './PhotoLightbox.css';

export interface LightboxImage {
  src: string;
  alt?: string;
}

interface PhotoLightboxProps {
  title: string;
  website?: string;
  images: LightboxImage[];
  onClose: () => void;
}

/**
 * Full-screen photo lightbox for Lead Finder site photos.
 *
 * Owns its own active index + keyboard/swipe nav. Images that fail to load
 * (404 / hotlink-blocked) are dropped client-side via onError so the gallery
 * never shows a broken-image icon — see `failed` below.
 */
const PhotoLightbox: React.FC<PhotoLightboxProps> = ({ title, website, images, onClose }) => {
  const [index, setIndex] = useState(0);
  // Source URLs that failed to load — filtered out of the visible set.
  const [failed, setFailed] = useState<Set<string>>(() => new Set());
  const touchStartX = useRef<number | null>(null);

  const visible = useMemo(
    () => images.filter(im => !failed.has(im.src)),
    [images, failed],
  );

  // Clamp the index whenever the visible set shrinks (e.g. an image errored out).
  const safeIndex = visible.length === 0 ? 0 : Math.min(index, visible.length - 1);
  const current = visible[safeIndex];

  const goPrev = useCallback(() => {
    setIndex(i => {
      const n = visible.length;
      return n === 0 ? 0 : (Math.min(i, n - 1) - 1 + n) % n;
    });
  }, [visible.length]);

  const goNext = useCallback(() => {
    setIndex(i => {
      const n = visible.length;
      return n === 0 ? 0 : (Math.min(i, n - 1) + 1) % n;
    });
  }, [visible.length]);

  // Keyboard nav: ← → to move, Esc to close.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') goPrev();
      else if (e.key === 'ArrowRight') goNext();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, goPrev, goNext]);

  // Every image errored out — nothing left to show, so close.
  useEffect(() => {
    if (images.length > 0 && visible.length === 0) onClose();
  }, [images.length, visible.length, onClose]);

  const markFailed = (src: string) =>
    setFailed(prev => {
      const next = new Set(prev);
      next.add(src);
      return next;
    });

  // DEBUG: filename (last path segment) shown under the carousel so we can eyeball
  // which images to filter out. Remove once the filtering rules are settled.
  const fileName = (src: string) => {
    try {
      return decodeURIComponent(new URL(src).pathname.split('/').pop() || src);
    } catch {
      return src;
    }
  };

  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 40) (dx > 0 ? goPrev() : goNext());
    touchStartX.current = null;
  };

  if (!current) return null;

  return (
    <div className="photo-lightbox-overlay" onClick={onClose}>
      <div className="photo-lightbox" onClick={(e) => e.stopPropagation()}>
        <div className="photo-lightbox-header">
          <div className="photo-lightbox-title">
            <span className="photo-lightbox-business">{title}</span>
            {website && (
              <a
                href={website.startsWith('http') ? website : `https://${website}`}
                target="_blank"
                rel="noopener noreferrer"
                className="photo-lightbox-site"
                title="Open website"
              >
                <FaExternalLinkAlt aria-hidden /> Visit site
              </a>
            )}
          </div>
          <span className="photo-lightbox-counter">{safeIndex + 1} / {visible.length}</span>
          <button type="button" className="photo-lightbox-close" onClick={onClose} title="Close (Esc)">
            <FaTimes aria-hidden />
          </button>
        </div>

        <div className="photo-lightbox-stage" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
          {visible.length > 1 && (
            <button type="button" className="photo-lightbox-nav prev" onClick={goPrev} title="Previous (←)">
              <FaChevronLeft aria-hidden />
            </button>
          )}
          <a
            href={current.src}
            target="_blank"
            rel="noopener noreferrer"
            className="photo-lightbox-hero-link"
            title="Open full image"
          >
            <img
              className="photo-lightbox-hero"
              src={current.src}
              alt={current.alt || title}
              onError={() => markFailed(current.src)}
            />
          </a>
          {visible.length > 1 && (
            <button type="button" className="photo-lightbox-nav next" onClick={goNext} title="Next (→)">
              <FaChevronRight aria-hidden />
            </button>
          )}
        </div>

        {current.alt && <div className="photo-lightbox-caption">{current.alt}</div>}
        {/* DEBUG: source filename for the active image. */}
        <div className="photo-lightbox-filename">{fileName(current.src)}</div>

        {visible.length > 1 && (
          <div className="photo-lightbox-filmstrip">
            {visible.map((im, i) => (
              <button
                type="button"
                key={im.src}
                className={`photo-lightbox-thumb ${i === safeIndex ? 'active' : ''}`}
                onClick={() => setIndex(i)}
                title={fileName(im.src)}
              >
                <img src={im.src} alt={im.alt || ''} onError={() => markFailed(im.src)} />
                {/* DEBUG: filename under each thumb. */}
                <span className="photo-lightbox-thumb-name">{fileName(im.src)}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PhotoLightbox;
